/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import { useState } from 'react';
import { Network, GitBranch, RefreshCw, Plus, AlertCircle, ExternalLink, Check } from 'lucide-react';
import { useToast } from '../../../hooks/use-toast';
import { createVelocityRepo } from '../../../lib/api/dsa';
import type { DSAOrgSettings } from '@shared/types/organizationSettings';

interface GitCredentials {
  provider: 'github' | 'gitlab';
  accessToken: string;
  instanceUrl?: string;
}

interface VelocityRepoStatus {
  checked: boolean;
  exists: boolean;
  url?: string;
  error?: string;
}

interface DSAOrgSectionProps {
  settings: DSAOrgSettings;
  gitCredentials: GitCredentials | null;
  organizationId: string | undefined;
  organizationName: string | undefined;
  onChange: (settings: DSAOrgSettings) => void;
  onDirty: () => void;
}

export function DSAOrgSection({ 
  settings, 
  gitCredentials,
  organizationId,
  organizationName,
  onChange, 
  onDirty 
}: DSAOrgSectionProps) {
  const { toast } = useToast();
  const [showManualRepoInput, setShowManualRepoInput] = useState(false);
  const [isCreatingRepo, setIsCreatingRepo] = useState(false);
  const [repoStatus, setRepoStatus] = useState<VelocityRepoStatus>({ 
    checked: false, 
    exists: false 
  });

  const handleCreateVelocityRepo = async () => {
    if (!gitCredentials || !gitCredentials.accessToken) {
      toast({
        title: 'Source Control Not Configured',
        description: 'Please configure GitHub or GitLab in your user settings before creating a Velocity repository.',
        variant: 'destructive'
      });
      return;
    }
    
    if (!organizationName) {
      toast({
        title: 'Organization Required',
        description: 'No active organization found. Please ensure you belong to an organization.',
        variant: 'destructive'
      });
      return;
    }
    
    setIsCreatingRepo(true);
    setRepoStatus({ checked: false, exists: false });
    
    try {
      const result = await createVelocityRepo({
        orgName: organizationName,
        provider: gitCredentials.provider,
        accessToken: gitCredentials.accessToken,
        instanceUrl: gitCredentials.provider === 'gitlab' ? gitCredentials.instanceUrl : undefined,
        detectExisting: true,
        organizationId, // Pass org ID to persist URL to org settings
      });
      
      if (result.success && result.url) {
        setRepoStatus({
          checked: true,
          exists: true,
          url: result.url
        });
        
        // Update local state (server also persists to org settings)
        onChange({ ...settings, velocityRepoUrl: result.url });
        // Note: No need for onDirty() since server persists the URL automatically
        
        toast({
          title: result.detected ? 'Repository Detected' : 'Repository Created',
          description: result.detected 
            ? 'Found existing Velocity repository for your organization. Settings saved.'
            : `Successfully created Velocity repository on ${gitCredentials.provider === 'github' ? 'GitHub' : 'GitLab'}. Settings saved.`,
        });
      } else {
        setRepoStatus({
          checked: true,
          exists: false,
          error: result.error || 'Failed to create repository'
        });
        
        toast({
          title: 'Repository Creation Failed',
          description: result.error || 'Could not create the Velocity repository. Please check your access token permissions.',
          variant: 'destructive'
        });
      }
    } catch (error: any) {
      setRepoStatus({
        checked: true,
        exists: false,
        error: error.message || 'Failed to create repository'
      });
      
      toast({
        title: 'Error',
        description: error.message || 'An unexpected error occurred.',
        variant: 'destructive'
      });
    } finally {
      setIsCreatingRepo(false);
    }
  };

  return (
    <div style={{ 
      marginBottom: '32px',
      padding: '20px',
      background: 'linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)',
      borderRadius: '8px',
      border: '1px solid #c4b5fd'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
        <Network size={20} style={{ marginRight: '8px', color: '#7c3aed' }} />
        <h3 style={{ margin: 0, color: '#5b21b6' }}>Distributed System Agents (DSA)</h3>
      </div>
      
      <p style={{ fontSize: '13px', color: '#6d28d9', marginBottom: '16px' }}>
        DSA enables organization-wide AI agents that run across team members' machines using distributed 
        infrastructure. When enabled, Velocity instances can participate in running scheduled agents 
        for security audits, coverage analysis, architecture reviews, and more.
      </p>
      
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '16px' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={settings.enabled}
            onChange={(e) => {
              onChange({ ...settings, enabled: e.target.checked });
              onDirty();
            }}
          />
          <span style={{ fontSize: '14px', color: '#5b21b6', fontWeight: 500 }}>Enable DSA for this organization</span>
        </label>
        <small style={{ color: '#7c3aed', fontSize: '12px', marginLeft: '24px', marginTop: '-8px' }}>
          When enabled, you'll need to configure a Velocity repository to coordinate DSA across your organization
        </small>
      </div>
      
      {settings.enabled && (
        <div style={{ 
          padding: '16px', 
          background: '#fff',
          borderRadius: '6px',
          border: '1px solid #ddd6fe'
        }}>
          {/* Velocity Repository Configuration */}
          <div style={{ marginBottom: '20px' }}>
            <h4 style={{ margin: '0 0 12px', fontSize: '14px', fontWeight: 600, color: '#5b21b6' }}>
              Velocity Repository
            </h4>
            
            <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '12px' }}>
              DSA uses a Git repository to coordinate agents across your organization. 
              You can create a new repository or connect to an existing one.
            </p>
            
            {/* Show configured repo if we have one */}
            {settings.velocityRepoUrl && !showManualRepoInput ? (
              <div style={{ 
                padding: '12px',
                background: '#f0fdf4',
                border: '1px solid #bbf7d0',
                borderRadius: '6px',
                marginBottom: '12px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#16a34a', fontSize: '13px' }}>
                    <Check size={16} />
                    <span>Velocity Repository Configured</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowManualRepoInput(true)}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#7c3aed',
                      fontSize: '12px',
                      cursor: 'pointer',
                      textDecoration: 'underline'
                    }}
                  >
                    Change
                  </button>
                </div>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '6px', 
                  marginTop: '8px',
                  fontSize: '13px',
                  color: '#15803d'
                }}>
                  <GitBranch size={14} />
                  <a 
                    href={settings.velocityRepoUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    style={{ color: '#15803d', textDecoration: 'underline' }}
                  >
                    {settings.velocityRepoUrl}
                  </a>
                  <ExternalLink size={12} />
                </div>
              </div>
            ) : (
              <div style={{ 
                padding: '16px',
                background: '#fefce8',
                border: '1px solid #fef08a',
                borderRadius: '6px',
                marginBottom: '12px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#a16207', fontSize: '13px', marginBottom: '12px' }}>
                  <AlertCircle size={16} />
                  <span>No Velocity repository configured</span>
                </div>
                
                {/* Error display */}
                {repoStatus.error && (
                  <div style={{ 
                    padding: '10px 12px',
                    background: '#fef2f2',
                    border: '1px solid #fecaca',
                    borderRadius: '4px',
                    marginBottom: '12px',
                    fontSize: '13px',
                    color: '#b91c1c'
                  }}>
                    <strong>Error:</strong> {repoStatus.error}
                  </div>
                )}
                
                {/* Show configured provider info or warning if not configured */}
                {!gitCredentials || !gitCredentials.accessToken ? (
                  <div style={{ 
                    padding: '12px',
                    background: '#fef2f2',
                    border: '1px solid #fecaca',
                    borderRadius: '6px',
                    marginBottom: '12px'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#b91c1c', fontSize: '13px', marginBottom: '8px' }}>
                      <AlertCircle size={16} />
                      <span><strong>Source control not configured</strong></span>
                    </div>
                    <p style={{ color: '#7f1d1d', fontSize: '13px', margin: 0 }}>
                      Please configure GitHub or GitLab in your <strong>user settings</strong> (Settings → Source Control) 
                      before creating a Velocity repository.
                    </p>
                  </div>
                ) : (
                  <>
                    {/* Show configured provider info */}
                    <div style={{ 
                      padding: '12px',
                      background: '#f3f4f6',
                      borderRadius: '6px',
                      marginBottom: '12px'
                    }}>
                      <div style={{ fontSize: '13px', color: '#374151' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                          <strong>Provider:</strong> 
                          <span style={{ 
                            background: gitCredentials.provider === 'github' ? '#1f2937' : '#fc6d26', 
                            color: 'white', 
                            padding: '2px 8px', 
                            borderRadius: '4px',
                            fontSize: '12px'
                          }}>
                            {gitCredentials.provider === 'github' ? 'GitHub' : 'GitLab'}
                          </span>
                          {gitCredentials.provider === 'gitlab' && gitCredentials.instanceUrl && (
                            <span style={{ color: '#6b7280', fontSize: '12px' }}>
                              ({gitCredentials.instanceUrl})
                            </span>
                          )}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <strong>Organization:</strong> 
                          <span>{organizationName || 'Not set'}</span>
                        </div>
                      </div>
                      <small style={{ color: '#6b7280', fontSize: '12px', display: 'block', marginTop: '8px' }}>
                        Repository will be named: <code style={{ background: '#e5e7eb', padding: '2px 6px', borderRadius: '4px' }}>
                          velocity-{(organizationName || 'org').toLowerCase().replace(/[^a-z0-9-]/g, '-')}
                        </code>
                      </small>
                    </div>
                    
                    {/* Create button */}
                    <button
                      type="button"
                      onClick={handleCreateVelocityRepo}
                      disabled={isCreatingRepo}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '10px 16px',
                        background: '#7c3aed',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '14px',
                        fontWeight: 500,
                        cursor: isCreatingRepo ? 'wait' : 'pointer',
                        opacity: isCreatingRepo ? 0.7 : 1
                      }}
                    >
                      {isCreatingRepo ? (
                        <>
                          <RefreshCw size={16} className="spin" />
                          Creating Repository...
                        </>
                      ) : (
                        <>
                          <Plus size={16} />
                          Create Velocity Repository
                        </>
                      )}
                    </button>
                    
                    <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '12px', marginBottom: 0 }}>
                      The Velocity repository will be created on {gitCredentials.provider === 'github' ? 'GitHub' : 'GitLab'} 
                      {' '}and initialized with the DSA directory structure.
                    </p>
                  </>
                )}
              </div>
            )}
            
            {/* Manual URL Input Toggle */}
            <div style={{ marginTop: '12px' }}>
              <button
                type="button"
                onClick={() => setShowManualRepoInput(!showManualRepoInput)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#7c3aed',
                  fontSize: '12px',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  padding: 0
                }}
              >
                {showManualRepoInput ? 'Hide manual URL input' : 'Or enter existing repository URL manually'}
              </button>
            </div>
            
            {/* Manual URL Input */}
            {showManualRepoInput && (
              <div className="form-group" style={{ marginTop: '12px', marginBottom: 0 }}>
                <label htmlFor="dsa-velocity-repo">Repository URL</label>
                <input
                  id="dsa-velocity-repo"
                  type="url"
                  placeholder="https://github.com/your-org/velocity-repo"
                  value={settings.velocityRepoUrl || ''}
                  onChange={(e) => {
                    onChange({ ...settings, velocityRepoUrl: e.target.value || undefined });
                    onDirty();
                  }}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                />
                <small style={{ color: '#666', fontSize: '12px', display: 'block', marginTop: '4px' }}>
                  Enter the URL of an existing Velocity repository.
                </small>
                {settings.velocityRepoUrl && (
                  <button
                    type="button"
                    onClick={() => setShowManualRepoInput(false)}
                    style={{
                      marginTop: '8px',
                      background: '#5b21b6',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      padding: '6px 12px',
                      fontSize: '13px',
                      cursor: 'pointer'
                    }}
                  >
                    Use this URL
                  </button>
                )}
              </div>
            )}
          </div>
          
          {/* User Participation Settings */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '16px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={settings.allowUserParticipation}
                onChange={(e) => {
                  onChange({ ...settings, allowUserParticipation: e.target.checked });
                  onDirty();
                }}
              />
              <span style={{ fontSize: '14px' }}>Allow users to control their own participation</span>
            </label>
            <small style={{ color: '#666', fontSize: '12px', marginLeft: '24px', marginTop: '-8px' }}>
              When enabled, individual users can choose whether to participate in DSA. 
              When disabled, DSA is automatically enabled for all users in the organization.
            </small>
          </div>
          
          {/* Advanced Settings - Collapsible */}
          <details style={{ marginTop: '16px' }}>
            <summary style={{ 
              cursor: 'pointer', 
              fontSize: '13px', 
              fontWeight: 500, 
              color: '#6b7280',
              marginBottom: '12px'
            }}>
              Advanced Settings
            </summary>
            
            <div style={{ 
              padding: '12px',
              background: '#f9fafb',
              borderRadius: '6px',
              marginTop: '8px'
            }}>
              <div className="form-group">
                <label htmlFor="dsa-poll-interval">Default Worker Poll Interval</label>
                <select
                  id="dsa-poll-interval"
                  value={settings.defaultPollIntervalMs}
                  onChange={(e) => {
                    onChange({ ...settings, defaultPollIntervalMs: parseInt(e.target.value) });
                    onDirty();
                  }}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                >
                  <option value={60000}>1 minute</option>
                  <option value={300000}>5 minutes (default)</option>
                  <option value={600000}>10 minutes</option>
                  <option value={900000}>15 minutes</option>
                  <option value={1800000}>30 minutes</option>
                </select>
                <small style={{ color: '#666', fontSize: '12px', display: 'block', marginTop: '4px' }}>
                  Default interval for DSA workers to check for agents to run
                </small>
              </div>
              
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label htmlFor="dsa-min-score">Default Minimum Eligibility Score</label>
                <input
                  id="dsa-min-score"
                  type="number"
                  min={0}
                  max={1}
                  step={0.1}
                  value={settings.defaultMinimumScore}
                  onChange={(e) => {
                    onChange({ ...settings, defaultMinimumScore: parseFloat(e.target.value) });
                    onDirty();
                  }}
                  style={{
                    width: '120px',
                    padding: '8px 12px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                />
                <small style={{ color: '#666', fontSize: '12px', display: 'block', marginTop: '4px' }}>
                  Agents must score above this threshold (0.0-1.0) to be executed by workers
                </small>
              </div>
            </div>
          </details>
          
          <div style={{ 
            marginTop: '16px',
            padding: '12px',
            background: '#f5f3ff',
            borderRadius: '4px',
            border: '1px solid #ddd6fe',
            fontSize: '13px',
            color: '#6d28d9'
          }}>
            <strong>How DSA Works:</strong>
            <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
              <li>DSA uses Git as a coordination mechanism - no central server required</li>
              <li>Organization members contribute compute from their machines</li>
              <li>Agents run on schedules (daily security audits, weekly reviews, etc.)</li>
              <li>Results are stored in the Velocity repository for full audit trail</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}
